import React, { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Check,
  Sparkles,
  Play,
  Timer,
  Fingerprint,
  Repeat,
  Banknote,
  ToggleRight,
  ArrowRight,
  ArrowDown,
  ShieldCheck,
  BadgeCheck,
  Clock,
} from "lucide-react";
import { goToAppStore, API } from "../lib/api";

const VIDEO_SRC = `${API}/media/how-it-works-stylists`;
const BEACH_HERO = process.env.PUBLIC_URL + "/stylist-beach-hero-v3.jpg";

const gbp = (n, plus = false) =>
  "£" +
  n.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) +
  (plus ? "+" : "");

const TIERS = [
  { name: "Pilot / Trial", tag: "Trial phase", rate: 5.5, hourly: 33.0 },
  { name: "Tier 3", tag: "Standard verified", rate: 7.4, hourly: 44.4 },
  { name: "Tier 2", tag: "High rating / KPI", rate: 8.88, hourly: 53.28 },
  { name: "Tier 1", tag: "Elite senior stylist", rate: 10.65, hourly: 63.9, plus: true },
];

const TRUST_BADGES = [
  { icon: BadgeCheck, text: "NVQ Level 2 required" },
  { icon: ShieldCheck, text: "5 years experience as a stylist required" },
  { icon: Banknote, text: "Fast payouts via Stripe Connect (£20 threshold)" },
  { icon: Clock, text: "Consultations capped at a 10 minute maximum, most under 5" },
];

const VIDEO_STEPS = [
  { n: "01", title: "Toggle active", desc: "Set your availability whenever you have free time behind the chair or at home." },
  { n: "02", title: "Instant DNA review", desc: "Receive the client's hair history, photos and concerns before you open the chat." },
  { n: "03", title: "Advise & recommend", desc: "Guide the client inside a 10 minute maximum window, most wrap up in under 5, with matching in stock retail products surfaced straight into the chat." },
  { n: "04", title: "Instant credit", desc: "Get immediate confirmation of your earnings, credited straight to your dashboard." },
];

const FEATURES = [
  {
    icon: ToggleRight,
    title: "Total availability autonomy",
    desc: "No forced shifts or appointment conflicts. A single master toggle routes inbound requests when you are on, and goes silent with zero penalties when you are off.",
  },
  {
    icon: Timer,
    title: "Protected 10 minute maximum, traffic light paced",
    desc: "A visual pacing bar keeps advice concise: Green (0 to 7 min), Amber (7 to 9 min), Red (9 to 10 min). Sessions are capped at 10 minutes and most finish in under 5, so clients cannot drag out free advice.",
  },
  {
    icon: Fingerprint,
    title: "Style DNA pre consultation dossier",
    desc: "Client texture, porosity, chemical history, reference photos and goals arrive upfront. No repetitive baseline discovery questions.",
  },
  {
    icon: Sparkles,
    title: "In chat product suggestions",
    desc: "The system reads the consultation and surfaces 3 relevant in stock retail products straight into chat. Zero manual searches or link building.",
  },
  {
    icon: Repeat,
    title: "Algorithmic client repeat queue",
    desc: "5 star ratings and quick replies keep you in the top 5 recommendation cards, building a loyal, recurring digital clientele.",
  },
  {
    icon: Banknote,
    title: "Automated Stripe Connect payouts",
    desc: "Full earnings history with self service bank transfers once your balance reaches £20. No invoicing, chasing payments or chargeback risk.",
  },
];

const ELIGIBILITY = [
  { title: "Accreditation benchmark", desc: "NVQ Level 2 required as an absolute baseline. NVQ Level 3 strongly preferred for advanced women's cut and colour." },
  { title: "Experience requirement", desc: "Minimum of 5 verifiable years active behind the chair in an established salon or reputable freelance environment." },
  { title: "Real time verification", desc: "Automated submission of photo identification, CV and professional references." },
  { title: "Approval velocity", desc: "Our live automated verification stack completes review in as little as 5 minutes." },
];

const ONBOARDING = [
  { n: "01", title: "Account & credential intake", desc: "Enter your professional details, upload your CV and provide one verified salon reference." },
  { n: "02", title: "Real time identity check", desc: "A quick automated ID scan confirms your qualification and right to work within minutes." },
  { n: "03", title: "Style DNA profile", desc: "Select your core specialties (Balayage, Vivids, Textured & Curly, Precision Fades, Scalp Care) to optimise matching." },
  { n: "04", title: "Connect Stripe & go live", desc: "Link your UK bank via Stripe Connect, flip your status to Available, and receive your first consultation." },
];

const reveal = {
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] },
};

function IncomeLadder() {
  const [perDay, setPerDay] = useState(3);
  const monthly = (rate) => rate * perDay * 30;

  return (
    <div>
      <div className="max-w-md mx-auto mb-10 text-center">
        <div className="flex items-center justify-between text-sm text-white/60">
          <span>Consultations per day</span>
          <span className="font-mono text-stylist-gold text-base">{perDay}</span>
        </div>
        <input
          type="range"
          min="1"
          max="6"
          value={perDay}
          onChange={(e) => setPerDay(Number(e.target.value))}
          data-testid="income-slider"
          className="mt-3 w-full accent-[#C59B27] cursor-pointer"
        />
        <p className="mt-2 text-xs text-white/40">
          Drag to see your monthly upside across 30 working days
        </p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {TIERS.map((t, i) => (
          <motion.div
            key={t.name}
            {...reveal}
            transition={{ ...reveal.transition, delay: i * 0.07 }}
            className={
              "rounded-2xl border p-6 " +
              (t.plus
                ? "border-stylist-gold/50 bg-stylist-gold/[0.06]"
                : "border-white/10 bg-white/[0.03]")
            }
            data-testid={`tier-${i + 1}`}
          >
            <p className="text-[10px] font-mono uppercase tracking-widest text-stylist-gold">{t.tag}</p>
            <h3 className="mt-1 font-serif text-2xl text-white">{t.name}</h3>
            <div className="mt-5 space-y-3 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-white/45">Per session (10 min cap)</span>
                <span className="text-white font-medium">{gbp(t.rate, t.plus)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-white/45">Effective hourly</span>
                <span className="text-white font-medium">{gbp(t.hourly, t.plus)}/hr</span>
              </div>
            </div>
            <div className="mt-5 pt-5 border-t border-white/10">
              <p className="text-[10px] font-mono uppercase tracking-widest text-white/40">
                Monthly upside
              </p>
              <p className="mt-1 font-serif text-3xl text-stylist-gold tabular-nums">
                {gbp(monthly(t.rate), t.plus)}
              </p>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

export default function StylistLanding() {
  const [showBar, setShowBar] = useState(false);
  const [params] = useSearchParams();

  useEffect(() => {
    // Persist any ambassador referral carried in from /stylist-invite so it
    // stays attached through the onboarding flow.
    const ref = (params.get("ref") || "").trim();
    if (ref) {
      try {
        localStorage.setItem("ss_stylist_ref", ref);
        sessionStorage.setItem("ss_stylist_ref", ref);
      } catch (e) { /* storage unavailable */ }
    }
  }, [params]);

  useEffect(() => {
    document.title = "Become a Senior Stylist | Senior Stylist";
    window.scrollTo(0, 0);
    const onScroll = () => setShowBar(window.scrollY > 680);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollToVideo = () =>
    document.getElementById("how")?.scrollIntoView({ behavior: "smooth" });

  return (
    <div className="bg-stylist-ink text-white" data-testid="stylist-landing-page">
      {/* sticky header */}
      <header className="sticky top-0 z-40 backdrop-blur bg-stylist-ink/80 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="stylist-logo">
            <span className="w-9 h-9 rounded-full bg-white flex items-center justify-center">
              <img src="/logo.png" alt="Senior Stylist" className="w-6 h-6 object-contain" />
            </span>
            <span className="font-serif text-lg">Senior Stylist</span>
          </Link>
          <button
            onClick={goToAppStore}
            data-testid="header-cta"
            className="rounded-full bg-stylist-gold text-stylist-ink px-5 py-2.5 text-xs font-medium hover:bg-white transition-colors"
          >
            Apply as a Stylist
          </button>
        </div>
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden" data-testid="stylist-hero">
        <div className="absolute inset-0">
          <img src={BEACH_HERO} alt="An ornate barber chair on a tropical beach with the Senior Stylist app glowing like the sun in the centre of the seat" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-stylist-ink via-stylist-ink/85 via-40% to-transparent" />
          <div className="absolute inset-0 ss-grain opacity-[0.06] pointer-events-none" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-24 md:py-32 lg:py-40">
          <div className="max-w-xl text-white">
            <p className="font-mono text-[0.62rem] sm:text-[0.68rem] uppercase tracking-[0.24em] text-stylist-gold">
              Invitation only · verified senior stylists worldwide
            </p>

            <h1 className="mt-6 font-serif font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.03] tracking-tight text-white">
              Take your chair anywhere.{" "}
              <span className="italic text-stylist-gold">No salon walls, no borders, just your chair.</span>
            </h1>

            <p className="mt-6 text-base md:text-lg text-white/75 max-w-xl leading-relaxed">
              Ready to work without limits? We are seeking exceptional stylists for an invitation only,
              advanced tier trial. Monetise your downtime with protected text consultations, keep all of
              your salon work, and become a foundational global expert, all from your phone.
            </p>

            <div
              className="mt-7 inline-flex items-center gap-3 rounded-2xl border border-stylist-gold/40 bg-stylist-gold/10 backdrop-blur px-5 py-3"
              data-testid="stylist-offer-badge"
            >
              <span className="w-2.5 h-2.5 rounded-full bg-stylist-gold shrink-0" />
              <span className="text-sm text-white">
                <span className="font-semibold text-stylist-gold">Invitation only:</span>{" "}
                for senior stylists seeking global opportunities
              </span>
            </div>

            <div className="mt-9 flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <button
                onClick={goToAppStore}
                data-testid="hero-cta"
                className="group inline-flex items-center gap-2 rounded-full bg-stylist-gold text-stylist-ink px-8 py-4 text-sm font-medium hover:bg-white transition-colors duration-300"
              >
                Become a Founding Stylist
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
              <button
                onClick={scrollToVideo}
                data-testid="hero-see-how"
                className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-white transition-colors"
              >
                <Play className="w-4 h-4 text-stylist-gold" />
                Watch the 2 minute walkthrough
                <ArrowDown className="w-3.5 h-3.5" />
              </button>
            </div>

            <ul className="mt-10 space-y-2.5">
              {TRUST_BADGES.map((b) => (
                <li key={b.text} className="flex items-center gap-3 text-sm text-white/85">
                  <span className="w-6 h-6 shrink-0 rounded-full bg-stylist-gold/20 flex items-center justify-center">
                    <b.icon className="w-3.5 h-3.5 text-stylist-gold" />
                  </span>
                  {b.text}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* EARNING POTENTIAL */}
      <section className="py-16 md:py-24 bg-[#141416]">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <motion.div {...reveal} className="text-center max-w-2xl mx-auto mb-12">
            <span className="font-mono text-[0.68rem] uppercase tracking-[0.28em] text-stylist-gold">
              The income ladder
            </span>
            <h2 className="mt-3 font-serif text-3xl sm:text-4xl lg:text-5xl">
              Turn 3 daily chair gaps into real revenue
            </h2>
            <p className="mt-4 text-white/55">
              Monetising three daily chair gaps can add £400 to £800+ in monthly revenue, without
              extending your salon opening hours. Each session is capped at 10 minutes, and most
              wrap up in under 5, so your effective rate is often even higher.
            </p>
          </motion.div>

          <IncomeLadder />

          <motion.div
            {...reveal}
            className="mt-12 max-w-3xl mx-auto rounded-2xl border border-stylist-gold/25 bg-stylist-gold/[0.05] p-6 text-center"
            data-testid="transparency-callout"
          >
            <p className="text-sm text-white/70 leading-relaxed">
              <span className="text-stylist-gold font-medium">No monthly SaaS fees. No membership dues.</span>{" "}
              Your earnings are credited directly to your Stripe Connect portal on consultation
              sign off. Transfer funds to your UK bank as soon as your balance reaches £20.
            </p>
          </motion.div>
        </div>
      </section>

      {/* VIDEO */}
      <section id="how" className="py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 lg:px-16">
          <motion.div {...reveal} className="text-center max-w-2xl mx-auto mb-12">
            <span className="font-mono text-[0.68rem] uppercase tracking-[0.28em] text-stylist-gold">
              See it in action
            </span>
            <h2 className="mt-3 font-serif text-3xl sm:text-4xl lg:text-5xl">How it works</h2>
            <p className="mt-4 text-white/55">
              See how Senior Stylist turns 10 minutes of downtime into seamless, professional income
              from your phone or salon tablet.
            </p>
          </motion.div>

          <motion.div
            {...reveal}
            className="relative rounded-2xl overflow-hidden border border-white/12 aspect-video bg-black"
            data-testid="video-walkthrough"
            style={{ boxShadow: "0 0 80px rgba(197,155,39,0.12)" }}
          >
            <video
              className="absolute inset-0 w-full h-full object-contain"
              controls
              playsInline
              preload="auto"
              data-testid="video-player"
            >
              <source src={VIDEO_SRC} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </motion.div>
          <p className="mt-4 text-center text-sm text-white/40">
            Step by step overview of the stylist app, Style DNA matching, live pacing and instant payouts.
          </p>

          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {VIDEO_STEPS.map((s, i) => (
              <motion.div
                key={s.title}
                {...reveal}
                transition={{ ...reveal.transition, delay: i * 0.08 }}
                className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 hover:border-stylist-gold/40 transition-colors duration-500"
                data-testid={`video-step-${s.n}`}
              >
                <span className="font-mono text-xs tracking-[0.3em] text-stylist-gold">{s.n}</span>
                <h3 className="mt-4 font-serif text-lg leading-snug">{s.title}</h3>
                <p className="mt-2 text-sm text-white/55 leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="py-16 md:py-24 bg-[#141416]">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <motion.div {...reveal} className="max-w-2xl mb-14">
            <span className="font-mono text-[0.68rem] uppercase tracking-[0.28em] text-stylist-gold">
              Built for professionals
            </span>
            <h2 className="mt-3 font-serif text-3xl sm:text-4xl lg:text-5xl">
              Core features for professional stylists
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((f, i) => (
              <motion.div
                key={f.title}
                {...reveal}
                transition={{ ...reveal.transition, delay: (i % 3) * 0.08 }}
                className="rounded-2xl border border-white/10 bg-white/[0.03] p-7 hover:border-stylist-gold/40 hover:bg-white/[0.05] transition-all duration-500"
                data-testid={`feature-${i + 1}`}
              >
                <div className="w-12 h-12 rounded-full bg-stylist-gold/15 flex items-center justify-center">
                  <f.icon className="w-6 h-6 text-stylist-gold" />
                </div>
                <h3 className="mt-5 font-serif text-xl leading-snug">{f.title}</h3>
                <p className="mt-3 text-sm text-white/55 leading-relaxed">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ELIGIBILITY */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <div className="grid lg:grid-cols-12 gap-12 items-start">
            <motion.div {...reveal} className="lg:col-span-4">
              <span className="font-mono text-[0.68rem] uppercase tracking-[0.28em] text-stylist-gold">
                Strict standards
              </span>
              <h2 className="mt-3 font-serif text-3xl sm:text-4xl lg:text-5xl leading-tight">
                Eligibility & accreditation
              </h2>
              <div className="mt-8 rounded-2xl border border-white/12 bg-white/[0.04] p-7 flex items-center gap-4">
                <span className="w-12 h-12 shrink-0 rounded-full bg-stylist-gold/15 flex items-center justify-center">
                  <ShieldCheck className="w-6 h-6 text-stylist-gold" />
                </span>
                <p className="text-sm text-white/60">
                  Verified in as little as{" "}
                  <span className="text-stylist-gold font-medium">5 minutes</span> by our automated
                  accreditation stack.
                </p>
              </div>
            </motion.div>

            <div className="lg:col-span-8 grid gap-5">
              {ELIGIBILITY.map((e, i) => (
                <motion.div
                  key={e.title}
                  {...reveal}
                  transition={{ ...reveal.transition, delay: i * 0.07 }}
                  className="flex items-start gap-5 rounded-2xl border border-white/10 bg-white/[0.03] p-7"
                  data-testid={`eligibility-${i + 1}`}
                >
                  <span className="mt-0.5 w-6 h-6 shrink-0 rounded-full bg-stylist-gold/20 flex items-center justify-center">
                    <Check className="w-3.5 h-3.5 text-stylist-gold" />
                  </span>
                  <div>
                    <h3 className="font-serif text-xl">{e.title}</h3>
                    <p className="mt-1.5 text-sm text-white/55 leading-relaxed">{e.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ONBOARDING */}
      <section className="py-16 md:py-24 bg-[#141416]">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <motion.div {...reveal} className="text-center max-w-2xl mx-auto mb-14">
            <span className="font-mono text-[0.68rem] uppercase tracking-[0.28em] text-stylist-gold">
              Live in minutes
            </span>
            <h2 className="mt-3 font-serif text-3xl sm:text-4xl lg:text-5xl">
              Four steps to your digital chair
            </h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {ONBOARDING.map((s, i) => (
              <motion.div
                key={s.title}
                {...reveal}
                transition={{ ...reveal.transition, delay: i * 0.08 }}
                className="relative rounded-2xl border border-white/10 bg-white/[0.03] p-7"
                data-testid={`onboarding-${s.n}`}
              >
                <span className="font-serif text-4xl text-stylist-gold/30">{s.n}</span>
                <h3 className="mt-3 font-serif text-lg leading-snug">{s.title}</h3>
                <p className="mt-2 text-sm text-white/55 leading-relaxed">{s.desc}</p>
                {i < ONBOARDING.length - 1 && (
                  <ArrowRight className="hidden lg:block absolute top-1/2 -right-3 w-5 h-5 text-white/15" />
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* BOTTOM ANCHOR */}
      <section className="py-20 md:py-28 relative overflow-hidden">
        <div className="absolute inset-0 ss-grain opacity-[0.06] pointer-events-none" />
        <div
          className="absolute inset-0 opacity-60 pointer-events-none"
          style={{ background: "radial-gradient(ellipse at 50% 120%, rgba(197,155,39,0.22), transparent 60%)" }}
        />
        <motion.div {...reveal} className="relative max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
            Stop giving away free advice on social media.
          </h2>
          <p className="mt-6 text-lg text-white/60 max-w-xl mx-auto">
            Set up your free digital chair in under 5 minutes. Work when you want, earn what you
            deserve, and reach a global client base.
          </p>
          <div className="mt-10 flex justify-center">
            <button
              onClick={goToAppStore}
              data-testid="bottom-cta"
              className="group inline-flex items-center gap-2 rounded-full bg-stylist-gold text-stylist-ink px-9 py-4 text-sm font-medium hover:bg-white transition-colors duration-300"
            >
              Apply for Senior Stylist verification
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
          <p className="mt-10 text-[11px] font-mono uppercase tracking-[0.22em] text-white/40">
            Senior Stylist Ltd. · Stripe verified partner · Strict privacy &amp; data protection compliant
          </p>
        </motion.div>
      </section>

      {/* STICKY BAR */}
      <div
        className={
          "fixed bottom-0 inset-x-0 z-40 transition-transform duration-500 " +
          (showBar ? "translate-y-0" : "translate-y-full")
        }
        data-testid="sticky-cta-bar"
      >
        <div className="mx-auto max-w-3xl m-3 rounded-2xl bg-[#141416] border border-white/12 shadow-2xl px-5 py-3 flex items-center justify-between gap-4">
          <div className="leading-tight">
            <p className="text-sm font-medium text-white">Your free digital chair</p>
            <p className="text-xs text-white/50">Zero chair rent · payouts from £20 · 5 min setup</p>
          </div>
          <button
            onClick={goToAppStore}
            data-testid="sticky-cta-button"
            className="shrink-0 rounded-full bg-stylist-gold text-stylist-ink px-6 py-3 text-sm font-medium hover:bg-white transition-colors"
          >
            Apply now
          </button>
        </div>
      </div>
    </div>
  );
}
